import {
  Card,
  Dialog,
  DialogTitle,
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
} from "@material-ui/core";
import React, { useCallback, useMemo, useState } from "react";
import GavelIcon from "@material-ui/icons/Gavel";
import SettingsBackupRestoreIcon from "@material-ui/icons/SettingsBackupRestore";
import { DvirDefectDto } from "../types";
import {
  Button,
  CardContentInner,
  Confirm,
  FormInput,
  RadioButtonGroupInput,
  Record,
  TextInput,
  useTranslate,
} from "react-admin";
import { Form } from "react-final-form";
import { CompactFormToolbar } from "../../../common/form/CompactFormToolbar";
import SaveIcon from "@material-ui/icons/Save";
import "../../../common/styles/form-style.css";
import { useAxiosInstance } from "../../../../common/hooks/useAxiosInstance";
import {
  DvirHistoryApiFactory,
  ResolveDvirDefectDto,
} from "../../../../common/data/service-proxies";

interface Props {
  onClick?: (event: React.MouseEvent) => void;
  record?: DvirDefectDto & Record;
  onSuccess?: () => void;
}

export const ResolveDefectMenuItem = ({
  onClick,
  record,
  onSuccess,
}: Props) => {
  const translate = useTranslate();
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
  const axios = useAxiosInstance();

  const radioChoices = useMemo(
    () => [
      {
        id: 1,
        name: translate(
          "resources.assets-dvir-vehicle-history.labels.defectCorrected"
        ),
      },
      {
        id: 2,
        name: translate(
          "resources.assets-dvir-vehicle-history.labels.defectNoNeedToBeCorrected"
        ),
      },
    ],
    [translate]
  );

  const handleClick = useCallback((record: DvirDefectDto) => {
    if (record.isResolved) {
      setConfirmOpen(true);
    } else {
      setDialogOpen(true);
    }
  }, []);

  const handleFormSubmit = useCallback(
    (data: ResolveDvirDefectDto) => {
      if (!axios) {
        return;
      }

      if (!record?.id) {
        return;
      }

      DvirHistoryApiFactory(undefined, axios.defaults.baseURL, axios)
        .apiAppDvirvehicledefectsResolvePost({
          ...data,
          defectId: Number(record.id),
        })
        .then(() => {
          if (onSuccess) onSuccess();
        })
        .finally(() => setDialogOpen(false));
    },
    [record, axios, onSuccess]
  );

  const handleReopen = useCallback(() => {
    if (!axios) {
      return;
    }

    if (!record?.id) {
      return;
    }

    DvirHistoryApiFactory(undefined, axios.defaults.baseURL, axios)
      .apiAppDvirvehicledefectsReopenPost(Number(record.id))
      .then(() => {
        if (onSuccess) onSuccess();
      })
      .finally(() => setConfirmOpen(false));
  }, [record, onSuccess, axios]);

  if (!record) {
    return null;
  }

  const buttonLabel = record.isResolved ? "reopen" : "resolve";
  return (
    <>
      <MenuItem
        className="actions-menu-item"
        onClick={(e) => {
          handleClick(record);
          if (typeof onClick === "function") {
            onClick(e);
          }
        }}>
        <ListItemIcon>
          {record.isResolved ? <SettingsBackupRestoreIcon /> : <GavelIcon />}
        </ListItemIcon>
        <ListItemText>
          {translate(
            `resources.assets-dvir-vehicle-history.labels.${buttonLabel}`
          )}
        </ListItemText>
      </MenuItem>
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>
          {translate(
            "resources.assets-dvir-vehicle-history.labels.resolveDefect"
          )}
        </DialogTitle>
        <Divider />
        <Card>
          <Form
            onSubmit={handleFormSubmit}
            render={({ handleSubmit }) => {
              return (
                <div className="simple-form form-create-edit-drawer">
                  <form onSubmit={handleSubmit}>
                    <CardContentInner>
                      <FormInput
                        basePath=""
                        input={
                          <RadioButtonGroupInput
                            label=""
                            source="isCorrected"
                            choices={radioChoices}
                            row={false}
                          />
                        }
                        record={record}
                        resource=""
                        variant="outlined"
                        defaultValue={1}
                      />
                      <FormInput
                        basePath=""
                        input={
                          <TextInput
                            label="resources.assets-dvir-vehicle-history.labels.comment"
                            source="comment"
                            multiline
                          />
                        }
                        record={record}
                        resource=""
                        variant="outlined"
                      />
                    </CardContentInner>
                    <CompactFormToolbar>
                      <Button
                        variant="contained"
                        type="submit"
                        color="primary"
                        label="ra.action.save">
                        <SaveIcon />
                      </Button>
                    </CompactFormToolbar>
                  </form>
                </div>
              );
            }}
          />
        </Card>
      </Dialog>
      <Confirm
        isOpen={confirmOpen}
        title="resources.assets-dvir-vehicle-history.labels.confirmReopenTitle"
        content="resources.assets-dvir-vehicle-history.labels.confirmReopenMessage"
        onConfirm={handleReopen}
        onClose={() => setConfirmOpen(false)}
      />
    </>
  );
};
